/**
 * TOSS Web Application — the portal's viewport provider.
 *
 * Split from `useClientViewport.ts` for the reason this project has hit twice already: a module
 * that exports both a component and a hook breaks Fast Refresh, and the lint rule that says so is
 * the one thing keeping the dev loop honest.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  ClientViewportContext,
  readClientViewport,
  type ClientViewport,
} from './useClientViewport';

/**
 * Mounted once, by `ClientLayout`. Everything below it reads the same measurement.
 *
 * `resize` is deliberately NOT debounced the way `useResponsive` debounces it: the state only ever
 * holds four booleans, so a re-render happens when a boundary is actually crossed, not on every
 * pixel — the comparison below is what makes that true.
 */
export function ClientViewportProvider({ children }: { children: ReactNode }): JSX.Element {
  const [viewport, setViewport] = useState<ClientViewport>(readClientViewport);

  useEffect(() => {
    const onResize = (): void => {
      setViewport((current) => {
        const next = readClientViewport();
        const unchanged =
          current.isPhone === next.isPhone &&
          current.isTablet === next.isTablet &&
          current.isDesktop === next.isDesktop &&
          current.isWide === next.isWide;

        return unchanged ? current : next;
      });
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <ClientViewportContext.Provider value={viewport}>{children}</ClientViewportContext.Provider>
  );
}
